/**
 * LISS Surprise Scrunchie Set - manual two-color picker.
 *
 * One product, one full price, one cart line item. The two colors the
 * customer picks resolve to a single existing variant of this product
 * (matched via each variant's option1/option2), so /cart/add.js is only
 * ever called once, for one variant, quantity 1. Availability (including
 * the "same color twice" rule) is read entirely from this product's own
 * variant data - no inventory numbers are hardcoded here.
 */
;(function () {
  'use strict'

  function init() {
    const block = document.querySelector('[data-surprise-block]')
    if (!block) return

    const variantsScript = block.querySelector('[data-surprise-variants]')
    if (!variantsScript) return

    let variants = []
    try {
      variants = JSON.parse(variantsScript.textContent)
    } catch (error) {
      console.error('LISS Surprise Set: failed to parse variant data', error)
      return
    }

    const color1Buttons = Array.from(block.querySelectorAll('[data-color-option="1"]'))
    const color2Buttons = Array.from(block.querySelectorAll('[data-color-option="2"]'))
    const helper = block.querySelector('[data-surprise-helper]')
    const addToCartBtn = block.querySelector('[data-surprise-add-to-cart]')

    let selectedColor1 = null
    let selectedColor2 = null
    let isAddingToCart = false

    function findVariant(color1, color2) {
      return variants.find((variant) => variant.option1 === color1 && variant.option2 === color2)
    }

    function isStaticallySoldOut(button) {
      return button.dataset.available === 'false'
    }

    // Disable a Color 2 option only when picking it together with the
    // current Color 1 selection would exceed real inventory (e.g. the
    // same color twice with only 1 unit left). Statically sold-out colors
    // (0 units, any pairing) stay disabled regardless of the other pick.
    function updateOtherColorAvailability(buttons, selectedOtherColor, buildPair) {
      buttons.forEach((btn) => {
        const value = btn.dataset.colorValue
        let disabled = isStaticallySoldOut(btn)

        if (!disabled && selectedOtherColor) {
          const [color1, color2] = buildPair(value, selectedOtherColor)
          const variant = findVariant(color1, color2)
          disabled = !variant || !variant.available
        }

        btn.disabled = disabled
        btn.classList.toggle('is-disabled', disabled)
        btn.setAttribute('aria-disabled', String(disabled))
      })
    }

    function updateColor2Availability() {
      updateOtherColorAvailability(color2Buttons, selectedColor1, (value, color1) => [color1, value])
    }

    function updateColor1Availability() {
      updateOtherColorAvailability(color1Buttons, selectedColor2, (value, color2) => [value, color2])
    }

    function currentVariant() {
      if (!selectedColor1 || !selectedColor2) return null
      return findVariant(selectedColor1, selectedColor2)
    }

    function updateHelperAndButton() {
      const variant = currentVariant()

      if (addToCartBtn) {
        addToCartBtn.disabled = !(variant && variant.available) || isAddingToCart
      }

      if (helper) {
        if (!selectedColor1 && !selectedColor2) {
          helper.textContent = 'Choose 2 colors to build your set'
        } else if (!selectedColor1 || !selectedColor2) {
          helper.textContent = 'Choose one more color'
        } else if (!variant || !variant.available) {
          helper.textContent = "That combination isn't available - try a different color"
        } else {
          helper.textContent = ''
        }
      }
    }

    function selectColor(optionNumber, value, button) {
      const buttons = optionNumber === 1 ? color1Buttons : color2Buttons

      buttons.forEach((btn) => {
        const isSelected = btn === button
        btn.classList.toggle('is-selected', isSelected)
        btn.setAttribute('aria-pressed', String(isSelected))
      })

      if (optionNumber === 1) {
        selectedColor1 = value
      } else {
        selectedColor2 = value
      }

      // Presentation only: reflect the picked name in the "Color: X" line.
      const currentLabel = block.querySelector(`[data-current-color="${optionNumber}"]`)
      if (currentLabel) currentLabel.textContent = value

      updateColor1Availability()
      updateColor2Availability()
      updateHelperAndButton()
    }

    color1Buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return
        selectColor(1, btn.dataset.colorValue, btn)
      })
    })

    color2Buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return
        selectColor(2, btn.dataset.colorValue, btn)
      })
    })

    async function handleAddToCart() {
      if (isAddingToCart) return

      const variant = currentVariant()
      if (!variant || !variant.available) return

      isAddingToCart = true
      if (addToCartBtn) addToCartBtn.disabled = true

      const formData = new FormData()
      formData.append('id', String(variant.id))
      formData.append('quantity', '1')
      formData.append('properties[Color 1]', selectedColor1)
      formData.append('properties[Color 2]', selectedColor2)

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        })
        const result = await response.json()

        if (!response.ok) {
          throw new Error(result.description || result.message || 'Failed to add to cart')
        }

        window.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
        document.dispatchEvent(
          new CustomEvent('cart:item-added', {
            detail: { item: result },
          })
        )

        window.location.href = '/cart'
      } catch (error) {
        console.error('LISS Surprise Set: add to cart failed', error)
        if (helper) helper.textContent = 'Something went wrong - please try again'
        isAddingToCart = false
        updateHelperAndButton()
      }
    }

    addToCartBtn?.addEventListener('click', handleAddToCart)

    updateHelperAndButton()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
